/**
 * Taxes and service charges on a stay. Pure integer arithmetic (minor units), shared by the server and the booking forms so the
 * number a guest sees is the number that's billed.
 *
 * The base is the stay price AFTER discounts (`net`). An exclusive charge is added on top of it; an inclusive one is already inside
 * the quoted price, so it only shows how much of the price it is. Exclusive charges are worked out on the price without the
 * inclusive ones. Charges don't compound on each other.
 */
export type ChargeKind = "TAX" | "SERVICE_CHARGE";

export interface ChargeRule {
  name: string;
  kind: ChargeKind;
  /** Basis points: 1000 = 10%. */
  rate_bps: number;
  inclusive: boolean;
}

export interface ChargeLine extends ChargeRule {
  amount: number;
}

export interface ChargeBreakdown {
  lines: ChargeLine[];
  /** Part of `net` that is inclusive charges. */
  inclusiveTotal: number;
  /** Added on top of `net`. */
  exclusiveTotal: number;
  /** What the guest pays: net + exclusive charges. */
  total: number;
}

export function computeCharges(net: number, rules: ChargeRule[]): ChargeBreakdown {
  const base = Math.max(0, Math.round(net));
  const inclusiveBps = rules.filter((r) => r.inclusive).reduce((s, r) => s + r.rate_bps, 0);

  const amounts = new Map<ChargeRule, number>();
  let inclusiveTotal = 0;
  for (const r of rules) {
    if (!r.inclusive) continue;
    const a = Math.round((base * r.rate_bps) / (10_000 + inclusiveBps));
    amounts.set(r, a);
    inclusiveTotal += a;
  }
  const netExInclusive = base - inclusiveTotal;
  let exclusiveTotal = 0;
  for (const r of rules) {
    if (r.inclusive) continue;
    const a = Math.round((netExInclusive * r.rate_bps) / 10_000);
    amounts.set(r, a);
    exclusiveTotal += a;
  }
  return {
    lines: rules.map((r) => ({ ...r, amount: amounts.get(r) ?? 0 })),
    inclusiveTotal,
    exclusiveTotal,
    total: base + exclusiveTotal,
  };
}
